import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import SearchBox from 'components/common/SearchBox';
import { ChangeEvent, useState, useMemo } from 'react';
import { Alert, Col, Placeholder, Row } from 'react-bootstrap';
import { faFileExport, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from 'react-query';
import { GET_CUSTOMERS_REP_QUERY } from 'services/api/customer-rep';
import { RepTableColumns } from 'components/tables/CustomerRepresentative';
import { RepViewBreadcrumbItems } from 'data/commonData';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import GTable from 'components/tables/GTable';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSIONS } from 'constants/permissions';

const ViewRep = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    isLoading,
    isError,
    data: Rep
  } = useQuery('get_customer_reps', GET_CUSTOMERS_REP_QUERY, {
    staleTime: 3 * 60 * 60 * 1000
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = useMemo(() => {
    if (!Rep?.data) return [];

    return (
      Rep.data
        .filter((rep: any) => {
          const searchStr = searchTerm.toLowerCase();
          return (
            rep.firstName?.toLowerCase().includes(searchStr) ||
            rep.lastName?.toLowerCase().includes(searchStr) ||
            rep.title?.toLowerCase().includes(searchStr) ||
            rep.mobile?.toLowerCase().includes(searchStr) ||
            rep.client?.organizationName?.toLowerCase().includes(searchStr) ||
            rep.client?.address?.toLowerCase().includes(searchStr) ||
            rep.client?.state?.toLowerCase().includes(searchStr) ||
            rep.client?.country?.toLowerCase().includes(searchStr)
          );
        })
        .sort((a: any, b: any) => {
          return (
            a?.firstName?.localeCompare(b?.firstName ?? '') ||
            a?.lastName?.localeCompare(b?.lastName ?? '') ||
            a?.title?.localeCompare(b?.title ?? '') ||
            a?.mobile?.localeCompare(b?.mobile ?? '') ||
            a?.client?.organizationName?.localeCompare(
              b?.client?.organizationName ?? ''
            ) ||
            a?.client?.address?.localeCompare(b?.client?.address ?? '') ||
            a?.client?.state?.localeCompare(b?.client?.state ?? '') ||
            a?.client?.country?.localeCompare(b?.client?.country ?? '')
          );
        }) ?? []
    );
  }, [Rep?.data, searchTerm]);

  const handleExport = () => {
    try {
      const exportData =
        Rep?.data?.map((rep: any) => ({
          FirstName: rep.firstName || '',
          LastName: rep.lastName || '',
          Mobile: rep.mobile || '',
          Organization: rep.client?.organizationName || '',
          Address: rep.client?.address || '',
          State: rep.client?.state || '',
          Country: rep.client?.country || '',
          CompanyPhone: rep.client?.phoneNumber || '',
          CreatedAt: new Date(rep.createdAt).toLocaleDateString(),
          UpdatedAt: new Date(rep.updatedAt).toLocaleDateString()
        })) || [];

      const ws = XLSX.utils.json_to_sheet(exportData);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Representatives');

      XLSX.writeFile(wb, 'customer-representatives.xlsx');

      toast.success('File has been downloaded successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export file');
    }
  };
  const [canCreateRep, canUpdateRep, canDeleteRep] = usePermissions([
    PERMISSIONS.CUSTOMER.REPRESENTATIVE.CREATE,
    PERMISSIONS.CUSTOMER.REPRESENTATIVE.UPDATE,
    PERMISSIONS.CUSTOMER.REPRESENTATIVE.DELETE
  ]);

  const columns = RepTableColumns(canUpdateRep, canDeleteRep);

  return (
    <div>
      <PageBreadcrumb items={RepViewBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">All Representative</h2>
        {isError && <Alert variant="danger">Error fetching customers</Alert>}
        <div className="mb-4">
          <Row className="g-3">
            <Col xs="auto">
              <SearchBox
                placeholder="Search Representative"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
            </Col>
            <Col
              xs="auto"
              className="scrollbar overflow-hidden-y flex-grow-1"
            ></Col>
            <Col xs="auto">
              {canCreateRep && (
                <Button
                  variant="link"
                  className="text-900 me-4 px-0"
                  onClick={handleExport}
                >
                  <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                  Export
                </Button>
              )}
              {canCreateRep && (
                <Link to="/home/apps/representative/create-rep">
                  <Button variant="primary">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Representative
                  </Button>
                </Link>
              )}
            </Col>
          </Row>
        </div>

        <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
          {isLoading ? (
            <div className="g-3 mt-8 mb-8">
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
              <Placeholder as="p" animation="glow">
                <Placeholder xs={12} />
              </Placeholder>
            </div>
          ) : (
            <GTable
              data={filteredData || []}
              columns={columns}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRep;
